import React, { useState, useEffect } from 'react';
import RealTimeMonitor from './RealTimeMonitor';
import { NotificationSystem } from './NotificationSystem';
import InsightsEngine from './InsightsEngine';
import { ABTest, TestStatus, ABTestHistory } from '../../../types/template';
import { WinnerDetectionResult, WinnerDetectionAlert } from '../../../services/winnerDetectionEngine';
import { ImplementationResult } from '../../../services/automatedImplementationService';
import { tone, toneForStatus } from "components/ui/tone";
import { clickable } from "components/ui/interactive";

const LiveMonitoringDemo: React.FC = () => {
  const [selectedTest, setSelectedTest] = useState<ABTest | null>(null);
  const [activeView, setActiveView] = useState<'monitor' | 'insights' | 'history'>('monitor');
  const [, setAlerts] = useState<WinnerDetectionAlert[]>([]);

  // Demo test data
  const demoTests: ABTest[] = [
    {
      id: 'test-1',
      name: 'Holiday CTA Button Test',
      templateId: 'template-1',
      templateName: 'Holiday Promotion Email',
      status: 'running' as TestStatus,
      createdAt: new Date('2024-07-10'),
      startedAt: new Date('2024-07-11'),
      estimatedCompletionAt: new Date('2024-07-25'),
      testableElements: [
        {
          blockId: 'cta-button-1',
          blockType: 'button',
          elementName: 'Primary CTA Button',
          testType: 'cta',
          suggestions: [],
          priority: 'high',
          isMarked: true,
        }
      ],
      variants: [
        {
          id: 'control',
          name: 'Control (Blue Button)',
          isControl: true,
          trafficAllocation: 50,
          template: {} as any,
          changes: []
        },
        {
          id: 'variant-a',
          name: 'Variant A (Orange Button)',
          isControl: false,
          trafficAllocation: 50,
          template: {} as any,
          changes: [
            {
              blockId: 'cta-button-1',
              changeType: 'style',
              description: 'Changed button color to orange',
              oldValue: { backgroundColor: '#3B82F6' },
              newValue: { backgroundColor: '#F97316' }
            }
          ]
        }
      ],
      metrics: {
        primaryMetric: {
          name: 'Click Rate',
          control: { value: 3.2, sampleSize: 1250 },
          variants: [{ value: 4.1, sampleSize: 1180 }],
          improvement: 28.1,
          significance: 0.92,
          confidenceInterval: [15.2, 41.0]
        },
        secondaryMetrics: [
          {
            name: 'Conversion Rate',
            control: { value: 1.8, sampleSize: 1250 },
            variants: [{ value: 2.3, sampleSize: 1180 }],
            improvement: 27.8,
            significance: 0.89
          }
        ]
      },
      configuration: {
        trafficSplit: [50, 50],
        successMetrics: [
          {
            id: 'click-rate',
            name: 'Click Rate',
            type: 'click_rate',
            isPrimary: true,
            weight: 1,
          }
        ],
        duration: { type: 'until_significant', days: 14 },
        statisticalSettings: {
          confidenceLevel: 95,
          minimumDetectableEffect: 10,
          powerAnalysis: 80,
        }
      }
    },
    {
      id: 'test-2',
      name: 'Subject Line A/B Test',
      templateId: 'template-2',
      templateName: 'Weekly Newsletter',
      status: 'completed' as TestStatus,
      createdAt: new Date('2024-06-15'),
      startedAt: new Date('2024-06-16'),
      completedAt: new Date('2024-06-30'),
      winner: 'variant-a',
      testableElements: [
        {
          blockId: 'subject-line-1',
          blockType: 'text',
          elementName: 'Email Subject Line',
          testType: 'content',
          suggestions: [],
          priority: 'high',
          isMarked: true,
        }
      ],
      variants: [
        { id: 'control', name: 'Control', isControl: true, trafficAllocation: 50, template: {} as any, changes: [] },
        { id: 'variant-a', name: 'Question Format', isControl: false, trafficAllocation: 50, template: {} as any, changes: [] }
      ],
      metrics: {
        primaryMetric: {
          name: 'Open Rate',
          control: { value: 22.5, sampleSize: 5000 },
          variants: [{ value: 28.3, sampleSize: 5000 }],
          improvement: 25.8,
          significance: 0.99,
          confidenceInterval: [18.2, 33.4]
        }
      },
      configuration: {
        trafficSplit: [50, 50],
        successMetrics: [
          {
            id: 'open-rate',
            name: 'Open Rate',
            type: 'open_rate',
            isPrimary: true,
            weight: 1,
          }
        ],
        duration: { type: 'fixed', days: 14 },
        statisticalSettings: {
          confidenceLevel: 95,
          minimumDetectableEffect: 15,
          powerAnalysis: 80,
        }
      }
    }
  ];

  // Demo test history
  const demoTestHistory: ABTestHistory = {
    templateId: 'template-1',
    tests: [
      {
        testId: 'test-1',
        testName: 'CTA Button Color Test',
        createdAt: new Date('2024-01-15'),
        completedAt: new Date('2024-01-22'),
        status: 'completed',
        winner: 'variant-b',
        primaryMetricImprovement: 23.5,
        testedElements: ['button-1'],
        insights: ['Orange buttons performed 23.5% better than blue buttons']
      }
    ],
    patterns: [
      {
        pattern: 'Action-oriented CTAs',
        frequency: 8,
        successRate: 75,
        averageImprovement: 18.3,
        examples: ['Get Started Now', 'Start Free Trial', 'Join Today']
      }
    ],
    recommendations: [
      {
        id: 'rec-1',
        title: 'Test CTA Button Text',
        description: 'Based on similar templates, testing CTA text variations typically shows 15-25% improvement',
        basedOn: ['test-1', 'test-2'],
        confidence: 85,
        expectedImprovement: 20,
        suggestedElements: ['button-1', 'button-2']
      }
    ]
  };

  // Set default selected test
  useEffect(() => {
    if (demoTests.length > 0 && !selectedTest) {
      setSelectedTest(demoTests[0]);
    }
  }, [selectedTest]);

  const handleWinnerDetected = (result: WinnerDetectionResult) => {
    console.log('Winner detected:', result);
    // In a real app, you might navigate to a results page or show a modal
  };

  const handleImplementationComplete = (result: ImplementationResult) => {
    console.log('Implementation complete:', result);
    // In a real app, you might show a success message or update the UI
  };

  const handleAlert = (alert: WinnerDetectionAlert) => {
    setAlerts(prev => [alert, ...prev].slice(0, 10));
    console.log('New alert:', alert);
  };

  const getStatusColor = (status) => tone(toneForStatus(status)).chip;

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Test Selection Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-surface rounded-xl shadow-lg border border-line-subtle p-4">
              <h3 className="text-lg font-medium text-content mb-4">Active Tests</h3>
              <div className="space-y-3">
                {demoTests.map((test) => (
                  <div
                    key={test.id}
                    {...clickable(() => setSelectedTest(test))}
                    className={`p-3 rounded-lg border cursor-pointer transition-colors ${selectedTest?.id === test.id
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                      : 'border-line hover:border-line-strong hover:bg-gray-50 dark:hover:bg-dark-700'
                      }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-medium text-content truncate">
                        {test.name}
                      </h4>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(test.status)}`}>
                        {test.status}
                      </span>
                    </div>
                    <p className="text-xs text-content-secondary mb-2">{test.templateName}</p>
                    {test.metrics?.primaryMetric && (
                      <div className="text-xs">
                        <span className={`font-medium ${test.metrics.primaryMetric.improvement > 0 ? 'text-green-600 dark:text-green-300' : 'text-red-600 dark:text-red-300'
                          }`}>
                          {test.metrics.primaryMetric.improvement > 0 ? '+' : ''}
                          {test.metrics.primaryMetric.improvement.toFixed(1)}%
                        </span>
                        <span className="text-content-muted ml-2">
                          ({Math.round(test.metrics.primaryMetric.significance * 100)}% confidence)
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* View Toggle */}
              <div className="mt-6 pt-4 border-t border-line">
                <h4 className="text-sm font-medium text-content mb-3">View Options</h4>
                <div className="space-y-2">
                  {[
                    { id: 'monitor', label: 'Real-Time Monitor', icon: '📊' },
                    { id: 'insights', label: 'AI Insights', icon: '🧠' },
                    { id: 'history', label: 'Test History', icon: '📈' }
                  ].map((view) => (
                    <button
                      key={view.id}
                      onClick={() => setActiveView(view.id as any)}
                      className={`w-full flex items-center space-x-2 px-3 py-2 text-sm rounded-lg transition-colors ${activeView === view.id
                        ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                        : 'text-content-secondary hover:bg-gray-100 dark:hover:bg-dark-700'
                        }`}
                    >
                      <span>{view.icon}</span>
                      <span>{view.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="lg:col-span-3">
            {selectedTest ? (
              <div>
                {activeView === 'monitor' && (
                  <RealTimeMonitor
                    test={selectedTest}
                    onWinnerDetected={handleWinnerDetected}
                    onImplementationComplete={handleImplementationComplete}
                    onAlert={handleAlert}
                  />
                )}

                {activeView === 'insights' && (
                  <InsightsEngine
                    test={selectedTest}
                    testHistory={demoTestHistory}
                  />
                )}

                {activeView === 'history' && (
                  <div className="bg-surface rounded-xl shadow-lg border border-line-subtle p-6">
                    <h3 className="text-lg font-semibold text-content mb-4">Test History & Patterns</h3>

                    {/* Historical Performance */}
                    <div className="space-y-4">
                      <div>
                        <h4 className="text-sm font-medium text-content mb-3">Previous Tests</h4>
                        <div className="space-y-3">
                          {demoTestHistory.tests.map((historyTest, index) => (
                            <div key={index} className="p-4 bg-gray-50 dark:bg-dark-700 rounded-lg">
                              <div className="flex items-center justify-between mb-2">
                                <h5 className="font-medium text-content">{historyTest.testName}</h5>
                                <span className="text-sm text-content-secondary">
                                  {historyTest.completedAt?.toLocaleDateString()}
                                </span>
                              </div>
                              <div className="flex items-center space-x-4 text-sm">
                                <span className="text-green-600 dark:text-green-300 font-medium">
                                  +{historyTest.primaryMetricImprovement}% improvement
                                </span>
                                <span className="text-content-secondary">
                                  Winner: {historyTest.winner}
                                </span>
                              </div>
                              {historyTest.insights.length > 0 && (
                                <div className="mt-2">
                                  <p className="text-sm text-gray-700 dark:text-gray-300">{historyTest.insights[0]}</p>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Patterns */}
                      <div>
                        <h4 className="text-sm font-medium text-content mb-3">Winning Patterns</h4>
                        <div className="space-y-2">
                          {demoTestHistory.patterns.map((pattern, index) => (
                            <div key={index} className="p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
                              <div className="flex items-center justify-between">
                                <span className="font-medium text-blue-900 dark:text-blue-300">{pattern.pattern}</span>
                                <span className="text-sm text-blue-700 dark:text-blue-300">
                                  {Math.round(pattern.successRate * 100)}% success rate
                                </span>
                              </div>
                              <div className="text-sm text-blue-800 dark:text-blue-300 mt-1">
                                Avg improvement: +{pattern.averageImprovement}%
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-surface rounded-lg shadow-sm border border-line p-12 text-center">
                <svg className="w-16 h-16 text-content-subtle mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <h3 className="text-lg font-medium text-content mb-2">Select a Test</h3>
                <p className="text-content-secondary">Choose a test from the sidebar to view its live monitoring dashboard.</p>
              </div>
            )}
          </div>
        </div>

        {/* Notification System */}
        <NotificationSystem
          notifications={[]}
          currentUser={{
            id: 'current-user',
            name: 'Current User',
            email: 'user@company.com',
            role: 'admin',
            permissions: ['view_test', 'manage_agents'],
            status: 'active',
            joinedAt: new Date(),
            lastActive: new Date(),
            preferences: {
              notifications: { email: true, inApp: true, types: ['test_completed'] },
              timezone: 'UTC',
              language: 'en'
            },
            metadata: {}
          }}
          onMarkAsRead={(notificationId) => {
            console.log(`Mark notification ${notificationId} as read`);
          }}
          onMarkAllAsRead={() => {
            console.log('Mark all notifications as read');
          }}
          onDismiss={(notificationId) => {
            console.log(`Dismiss notification ${notificationId}`);
          }}
          onAction={(notificationId, actionId) => {
            console.log(`Notification ${notificationId} action ${actionId}`);
          }}
        />
    </>
  );
};

export default LiveMonitoringDemo;